import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { repOptions } from "@/lib/leads";
import { linkedRep, quickAddDefaults } from "@/lib/linkedRep";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";

const RepPicker = ({ kind, value, reps, onChange }) => (
  <select data-testid={`quick-add-${kind}-select`} value={value} onChange={(e) => onChange(e.target.value)} className="inp flex-1 sm:w-44">
    <option value="">{kind === "setter" ? "Setter (optional)" : "Closer (optional)"}</option>
    {repOptions(reps, kind).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
  </select>
);

export const QuickAdd = () => {
  const { reps, upsertLead } = useData();
  const { user } = useAuth();
  const rep = linkedRep(user, reps);
  const defaults = quickAddDefaults(rep);
  const [name, setName] = useState("");
  const [setterId, setSetterId] = useState(defaults.setter);
  const [closerId, setCloserId] = useState(defaults.closer);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSetterId(defaults.setter); setCloserId(defaults.closer); }, [defaults.setter, defaults.closer]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError("Lead name is required");
    setBusy(true);
    try {
      const { data } = await api.post("/leads", { name: name.trim(), setter_id: setterId || null, closer_id: closerId || null });
      upsertLead(data);
      setName("");
      setError("");
      toast.success(`${data.name} added to New`);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="quick-add-form" className="w-full sm:w-auto">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <input data-testid="quick-add-name-input" value={name} placeholder="Quick-add a lead name"
          onChange={(e) => { setName(e.target.value); if (error) setError(""); }}
          className={`inp sm:w-64 ${error ? "inp-invalid" : ""}`} />
        <div className="flex gap-2">
          {rep?.role === "Closer"
            ? <RepPicker kind="closer" value={closerId} reps={reps} onChange={setCloserId} />
            : <RepPicker kind="setter" value={setterId} reps={reps} onChange={setSetterId} />}
          <button type="submit" disabled={busy} data-testid="quick-add-submit-btn" className="btn-primary shrink-0">
            <Plus className="h-4 w-4" /> Add lead
          </button>
        </div>
      </div>
      {error && <p data-testid="quick-add-error" className="field-error mt-1.5">{error}</p>}
    </form>
  );
};
